/**
 * Secure document hashing utilities using SHA-256.
 *
 * This module provides cryptographically secure hashing for document URLs
 * to prevent collisions and ensure deduplication integrity.
 *
 * Security Properties:
 * - SHA-256 provides 2^256 collision resistance (vs 2^32 for simple hash)
 * - 50% collision probability: ~65k documents (32-bit) vs ~10^38 documents (SHA-256)
 * - Deterministic output for identical normalized URLs
 * - Browser-compatible using Web Crypto API
 */

/**
 * Generate a SHA-256 hash of a URL for document deduplication.
 *
 * The URL is normalized before hashing to ensure consistent results:
 * - Protocol and host are lowercased
 * - Trailing slashes are removed from pathnames
 * - Invalid URLs fall back to simple normalization
 *
 * @param url - The document URL to hash
 * @returns Promise<string> - Hex-encoded SHA-256 hash (64 characters)
 *
 * @example
 * ```typescript
 * const hash = await generateDocumentHash('https://docs.example.com/guide/')
 * // Returns: '8f3b5c4d2e1a9f7e6d5c4b3a2e1f0e9d8c7b6a5e4d3c2b1a0f9e8d7c6b5a4e3d'
 * ```
 */
export async function generateDocumentHash(url: string): Promise<string> {
  // Normalize URL for consistent hashing
  const normalized = normalizeUrl(url);

  // Convert to UTF-8 bytes
  const encoder = new TextEncoder();
  const data = encoder.encode(normalized);

  // Compute SHA-256
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);

  // Convert to hex string
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");

  return hashHex;
}

/**
 * Normalize URL for consistent hashing.
 *
 * Normalization ensures that equivalent URLs produce the same hash:
 * - 'https://example.com/docs/' and 'https://example.com/docs' are identical
 * - 'HTTPS://EXAMPLE.COM/docs' and 'https://example.com/docs' are identical
 *
 * @param url - Raw URL string
 * @returns Normalized URL string
 *
 * @example
 * ```typescript
 * normalizeUrl('HTTPS://Docs.Example.COM/Guide/')
 * // Returns: 'https://docs.example.com/Guide'
 * ```
 */
function normalizeUrl(url: string): string {
  try {
    const parsed = new URL(url);

    // Lowercase protocol and host for case-insensitive matching
    parsed.protocol = parsed.protocol.toLowerCase();
    parsed.host = parsed.host.toLowerCase();

    // Remove trailing slash from pathname (except for root '/')
    if (parsed.pathname !== "/") {
      parsed.pathname = parsed.pathname.replace(/\/$/, "");
    }

    // Optional: Strip query parameters for documentation URLs
    // Uncomment the following line if docs should dedupe regardless of query params
    // parsed.search = ''

    // Optional: Strip fragment identifiers (anchors)
    // Uncomment the following line if docs should dedupe regardless of anchors
    // parsed.hash = ''

    return parsed.toString();
  } catch {
    // Fallback for invalid URLs: simple normalization
    return url.toLowerCase().replace(/\/$/, "");
  }
}

/**
 * Verify if a string is a valid SHA-256 hash.
 *
 * @param hash - String to validate
 * @returns boolean - True if valid SHA-256 hash format
 *
 * @example
 * ```typescript
 * isValidSHA256('8f3b5c4d2e1a9f7e6d5c4b3a2e1f0e9d8c7b6a5e4d3c2b1a0f9e8d7c6b5a4e3d')
 * // Returns: true
 *
 * isValidSHA256('abc123')
 * // Returns: false
 * ```
 */
export function isValidSHA256(hash: string): boolean {
  return /^[a-f0-9]{64}$/i.test(hash);
}
